/**
 * Клиент REST API.
 *
 * Все пути относительные: приложение всегда общается со своим собственным
 * origin. Резервные домены — это отдельные развёртывания, которые открывают
 * вручную; кросс-доменных запросов нет намеренно, иначе пришлось бы ослабить
 * CSP и перечислить запасные адреса прямо в заголовке — то есть подсказать их
 * тому, кто как раз и занимается блокировками.
 */
import type { PublicUser, SelfUser, WireChat, WireMessage } from './types.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(code);
  }
}

let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: {
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    // Сеть не поднялась — отличаем от ответа сервера, чтобы очередь исходящих
    // понимала, что нужно просто повторить позже.
    throw new ApiError(0, 'network_unreachable');
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload.error === 'string' ? payload.error : 'unknown_error',
      payload,
    );
  }
  return payload as T;
}

export interface RegisterInput {
  username: string;
  displayName: string;
  inviteCode?: string;
  pwSalt: string;
  pwOps: number;
  pwMem: number;
  authKey: string;
  identityPublicKey: string;
  wrappedIdentitySecret: string;
  deviceLabel: string;
}

export const api = {
  passwordParams: (username: string) =>
    request<{ pwSalt: string; pwOps: number; pwMem: number }>(
      'GET',
      `/api/auth/params?username=${encodeURIComponent(username)}`,
    ),

  register: (input: RegisterInput) =>
    request<{ token: string; user: SelfUser }>('POST', '/api/auth/register', input),

  login: (username: string, authKey: string, deviceLabel: string) =>
    request<{ token: string; user: SelfUser }>('POST', '/api/auth/login', {
      username,
      authKey,
      deviceLabel,
    }),

  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),

  me: () => request<{ user: SelfUser; vapidPublicKey: string }>('GET', '/api/auth/me'),

  sessions: () =>
    request<{
      sessions: {
        id: string;
        deviceLabel: string;
        createdAt: number;
        lastSeenAt: number;
        current: boolean;
      }[];
    }>('GET', '/api/auth/sessions'),

  revokeSession: (id: string) => request<{ ok: true }>('DELETE', `/api/auth/sessions/${id}`),

  lookupUser: (username: string) =>
    request<{ user: PublicUser }>(
      'GET',
      `/api/users/lookup?username=${encodeURIComponent(username)}`,
    ),

  setDisplayName: (displayName: string) =>
    request<{ user: PublicUser }>('PATCH', '/api/users/me', { displayName }),

  createInvite: (ttlDays: number | null) =>
    request<{ code: string }>('POST', '/api/invites', { ttlDays }),

  listInvites: () =>
    request<{
      invites: {
        code: string;
        createdAt: number;
        usedAt: number | null;
        expiresAt: number | null;
        used: boolean;
      }[];
    }>('GET', '/api/invites'),

  chats: () => request<{ chats: WireChat[] }>('GET', '/api/chats'),

  chat: (chatId: string) => request<{ chat: WireChat }>('GET', `/api/chats/${chatId}`),

  /** Убирает чат из своего списка. У собеседника он остаётся. */
  deleteChat: (chatId: string) => request<{ ok: true }>('DELETE', `/api/chats/${chatId}`),

  createChat: (input: {
    kind: 'direct' | 'group';
    titleEnc: string | null;
    keys: { userId: string; sealedKey: string }[];
  }) => request<{ chat: WireChat; existed: boolean }>('POST', '/api/chats', input),

  addMember: (chatId: string, userId: string, sealedKey: string) =>
    request<{ members: PublicUser[] }>('POST', `/api/chats/${chatId}/members`, {
      userId,
      sealedKey,
    }),

  removeMember: (chatId: string, userId: string) =>
    request<{ ok: true; chatDeleted: boolean }>(
      'DELETE',
      `/api/chats/${chatId}/members/${userId}`,
    ),

  rekey: (chatId: string, keys: { userId: string; sealedKey: string }[]) =>
    request<{ chat: WireChat }>('POST', `/api/chats/${chatId}/rekey`, { keys }),

  setTitle: (chatId: string, titleEnc: string | null) =>
    request<{ chat: WireChat }>('PATCH', `/api/chats/${chatId}`, { titleEnc }),

  setMuted: (chatId: string, muted: boolean) =>
    request<{ ok: true; muted: boolean }>('POST', `/api/chats/${chatId}/mute`, { muted }),

  markRead: (chatId: string, seq: number) =>
    request<{ ok: true }>('POST', `/api/chats/${chatId}/read`, { seq }),

  markDelivered: (chatId: string, seq: number) =>
    request<{ ok: true }>('POST', `/api/chats/${chatId}/delivered`, { seq }),

  history: (chatId: string, params: { before?: number; after?: number; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.before !== undefined) query.set('before', String(params.before));
    if (params.after !== undefined) query.set('after', String(params.after));
    query.set('limit', String(params.limit ?? 50));
    return request<{ messages: WireMessage[]; hasMore: boolean }>(
      'GET',
      `/api/chats/${chatId}/messages?${query}`,
    );
  },

  send: (
    chatId: string,
    input: { keyEpoch: number; nonce: string; ciphertext: string; localId?: string },
  ) => request<{ message: WireMessage }>('POST', `/api/chats/${chatId}/messages`, input),

  editMessage: (
    chatId: string,
    messageId: string,
    input: { keyEpoch: number; nonce: string; ciphertext: string },
  ) => request<{ message: WireMessage }>('PATCH', `/api/chats/${chatId}/messages/${messageId}`, input),

  // ─── Аватарки ──────────────────────────────────────────────────────────────

  putAvatar: (avatarEnc: string, keys: { userId: string; sealedKey: string }[]) =>
    request<{ ok: true; updatedAt: number }>('PUT', '/api/users/me/avatar', { avatarEnc, keys }),

  deleteAvatar: () => request<{ ok: true }>('DELETE', '/api/users/me/avatar'),

  /** Кому ключ аватарки ещё не запечатан — появляется при новом общем чате. */
  avatarMissing: () => request<{ users: PublicUser[] }>('GET', '/api/users/me/avatar/missing'),

  addAvatarKeys: (keys: { userId: string; sealedKey: string }[]) =>
    request<{ ok: true }>('POST', '/api/users/me/avatar/keys', { keys }),

  getAvatar: (userId: string) =>
    request<{ avatarEnc: string; sealedKey: string; updatedAt: number }>(
      'GET',
      `/api/users/${userId}/avatar`,
    ),

  pushKey: () => request<{ publicKey: string }>('GET', '/api/push/key'),

  pushSubscribe: (subscription: PushSubscriptionJSON) =>
    request<{ ok: true }>('POST', '/api/push/subscribe', subscription),

  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: true }>('POST', '/api/push/unsubscribe', { endpoint }),

  pushTest: () => request<{ ok: true; sentTo: number }>('POST', '/api/push/test'),

  pushHealth: () =>
    request<{
      subscriptions: number;
      failing: number;
      neverDelivered: number;
      proxyEnabled: boolean;
    }>('GET', '/api/push/health'),
};
